import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../lib/api.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Minimum gap between proactive refreshes. Prevents every tab-switch
// blip from spamming /refresh — we only act when the gap since the last
// refresh exceeds this. Access tokens live 15m; 5m cadence keeps the
// session fresh without hammering the endpoint.
const MIN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

type UserRole = 'user' | 'admin';
type User = { id: string; email: string; username: string; role: UserRole };

interface AuthContextType {
  user: User | null;
  loading: boolean;
  bootstrap: () => Promise<void>;
  // login/register return the User so callers can navigate without
  // waiting for the next render — setUser is async and the closure
  // would otherwise hold a stale `user` value.
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, username: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  // `undefined` means "not yet fetched / attempted". That keeps ProtectedRoute
  // from redirecting before we've asked the server.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const loading = user === undefined;

  // Guard so repeated calls to `bootstrap()` (e.g. from multiple
  // ProtectedRoute mounts) produce a single /me request per session.
  // Also flipped by login/register so a subsequent ProtectedRoute mount
  // doesn't re-fetch when we already have a user.
  const bootstrapStarted = useRef(false);

  const bootstrap = useCallback(async () => {
    if (bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    try {
      setUser(await api.get<User>('/api/auth/me'));
    } catch {
      setUser(null);
    }
  }, []);

  // Bootstrap once on mount so the user state is populated on every
  // page load, not only on ProtectedRoute mounts. Public routes
  // (playground, viewer, login, register) need it too — the Toolbar's
  // zer0 home link picks /me vs /register based on `user`, and without
  // this effect an authed user landing directly on /playground would
  // be treated as anonymous and sent to /register.
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  async function login(email: string, password: string): Promise<User> {
    const data = await api.post<{ user: User }>('/api/auth/login', { email, password });
    bootstrapStarted.current = true;
    setUser(data.user);
    return data.user;
  }

  async function register(email: string, username: string, password: string): Promise<User> {
    const data = await api.post<{ user: User }>('/api/auth/register', {
      email,
      username,
      password,
    });
    bootstrapStarted.current = true;
    setUser(data.user);
    return data.user;
  }

  async function logout(): Promise<void> {
    await api.post('/api/auth/logout');
    setUser(null);
  }

  // Focus-triggered refresh. When the user returns to the tab (or focuses
  // the window) after being away, proactively refresh the access token so
  // their next click doesn't eat a round-trip on the lazy 401→refresh
  // path. Guarded by `lastRefreshRef` so rapid tab-switches don't spam
  // the endpoint. A hard 401 from the refresh endpoint means the refresh
  // token expired (>7d inactivity); drop the user so ProtectedRoute
  // redirects to /login.
  const lastRefreshRef = useRef<number>(Date.now());
  useEffect(() => {
    async function refreshIfStale(): Promise<void> {
      if (document.visibilityState !== 'visible') return;
      if (!user) return;
      const now = Date.now();
      if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) return;
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          lastRefreshRef.current = now;
        } else if (res.status === 401) {
          setUser(null);
        }
        // Other non-ok responses: leave state alone, retry on next focus.
      } catch {
        // Network error — don't log them out, they might be offline.
      }
    }
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', refreshIfStale);
    return () => {
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        loading,
        bootstrap,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
